import { useSelector } from 'react-redux';
import { Button, Form, Space, Tooltip } from 'antd';
import { CloseOutlined, ThunderboltOutlined } from '@ant-design/icons';
import TagsSelect from '../TagsSelect';
import { matchTagRule } from '../tagRuleMatch';

/**
 * The "Oznaka" row, shared by DefaultFields and PersonalFields.
 *
 * Both field groups render an identical tag row, and this one grew two pieces of
 * behaviour (the rule hint and the "always tag like this" shortcut), so it lives
 * in one place rather than being kept in sync by hand in two files.
 *
 * The prefill itself is NOT here -- it belongs to IntentDrawer, which owns the
 * form instance and the once-per-session latch. This component only renders
 * what IntentDrawer decided.
 *
 * Note the structure: `name="tag"` moves to a noStyle inner item so `extra` can
 * hang off the outer wrapper, the same shape the project-splits row uses.
 */
const TagField = ({ tagRuleHint, onClearTagRuleHint, onApplyTitleOverride, onCreateTagRule, massEdit }) => {
  const form = Form.useFormInstance();
  const titleValue = Form.useWatch('title', form);
  const tagValue = Form.useWatch('tag', form);
  const tagsById = useSelector((state) => state.Tags.byId);
  const tagRules = useSelector((state) => state.TagRules.list);

  // Offering to create a rule that already exists would just produce a
  // duplicate-pattern 400, so only offer when nothing matches today.
  const canCreateTagRule =
    !massEdit &&
    !!onCreateTagRule &&
    !!titleValue &&
    !!tagValue &&
    matchTagRule(titleValue, tagRules) == null;

  return (
    <Form.Item
      label="Oznaka"
      extra={
        tagRuleHint ? (
          <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
            <Space size={4}>
              <ThunderboltOutlined />
              <span>{`Predlagano po pravilu »${tagRuleHint.pattern}«`}</span>
              <Tooltip title="Odstrani predlog">
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onClearTagRuleHint}
                />
              </Tooltip>
            </Space>
            {/* Offered, not applied: the rule cannot rewrite the title while it
                is being typed without swallowing the next keystroke. */}
            {tagRuleHint.titleOverride && tagRuleHint.titleOverride !== titleValue && (
              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
                      onClick={onApplyTitleOverride}>
                {`Preimenuj v »${tagRuleHint.titleOverride}«`}
              </Button>
            )}
          </Space>
        ) : null
      }
    >
      <Space.Compact style={{ display: 'flex', width: '100%' }}>
        {/* noStyle: the wrapper above owns the label and the hint. */}
        <Form.Item name="tag" noStyle>
          <TagsSelect />
        </Form.Item>
        {canCreateTagRule && (
          <Tooltip
            title={`Vedno označi »${titleValue}« kot ${tagsById?.[tagValue]?.title ?? 'izbrano oznako'}`}
          >
            <Button icon={<ThunderboltOutlined />} onClick={onCreateTagRule} />
          </Tooltip>
        )}
      </Space.Compact>
    </Form.Item>
  );
};

export default TagField;
